import { useState, useEffect } from 'preact/hooks';
import type { ComponentChildren } from 'preact';
import { store } from '../store';
import { getConfig } from '../config';
import { getOrders, getOrder } from '../api';
import { formatPrice } from '../utils/currency';
import { getOrderStatusClass, getOrderStatusLabel, isOrderAttentionStatus } from '../utils/orderStatus';
import { formatTicketDateTime } from '../utils/timezone';
import { Pagination } from './Pagination';
import { OrderDetailsSkeleton } from './LoadingSkeletons';
import { t } from '../i18n';

function OrderDateCell({ value }: { value?: unknown }) {
  const { date, time, title } = formatTicketDateTime(value);
  return (
    <div class="sw-ticket-date" title={title}>
      <span class="sw-ticket-date__day">{date}</span>
      {time ? <span class="sw-ticket-date__time">{time}</span> : null}
    </div>
  );
}

function OrderRowChevron() {
  return (
    <svg class="sw-ticket-row-chevron" width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
      <path d="M9 6l6 6-6 6" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" />
    </svg>
  );
}

function DetailRow({ label, children }: { label: string; children: ComponentChildren }) {
  return (
    <div class="sw-order-kv">
      <dt class="sw-order-kv__label">{label}</dt>
      <dd class="sw-order-kv__value">{children}</dd>
    </div>
  );
}

export function OrdersView({ config }: { config: ReturnType<typeof getConfig> }) {
  const [data, setData] = useState<{ orders?: Array<Record<string, unknown>> } | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedOrderId, setSelectedOrderId] = useState<string | null>(null);
  const [filter, setFilter] = useState('all'); // all, M, W, D
  const [page, setPage] = useState(1);
  const itemsPerPage = 10;

  useEffect(() => {
    const focusId = store.consumeOrdersFocusOrderId();
    if (focusId) setSelectedOrderId(focusId);
  }, []);

  useEffect(() => {
    setLoading(true);
    const params: Record<string, string> = filter === 'all' ? {} : { date_period: filter };
    getOrders(config, params).then((res) => {
      setLoading(false);
      if (res.status === 200 && res.result) {
        const result = res.result as any;
        const rawOrders = Array.isArray(result) ? result : result.orders;
        setData({ orders: rawOrders });
        setPage(1);
      } else {
        setError(res.error ?? t('orders.loadFailed'));
      }
    });
  }, [config.apiUrl, filter]);

  if (selectedOrderId) {
    return <OrderDetailsView config={config} orderId={selectedOrderId} onBack={() => setSelectedOrderId(null)} />;
  }

  const rawOrders = data?.orders ?? [];
  const groupedOrders = rawOrders.reduce((acc: Record<string, any>, row: any) => {
    const id = row.sorderid || row.orderid || row.id;
    if (!acc[id]) {
      acc[id] = { ...row, products: [] };
    }
    acc[id].products.push(row);
    return acc;
  }, {});

  const orderList = Object.values(groupedOrders).sort((a: any, b: any) => {
    const dateA = Number(a.order_timestamp ?? a.date ?? 0) || new Date(a.order_date || 0).getTime() / 1000;
    const dateB = Number(b.order_timestamp ?? b.date ?? 0) || new Date(b.order_date || 0).getTime() / 1000;
    return dateB - dateA;
  });

  const totalPages = Math.ceil(orderList.length / itemsPerPage);
  const paginatedOrders = orderList.slice((page - 1) * itemsPerPage, page * itemsPerPage);

  const openOrder = (id: string) => setSelectedOrderId(id);

  if (loading) {
    return (
      <div class="sw-tickets-shell" data-sw-ui="orders-v3">
        <div class="sw-page-chrome placeholder-glow" aria-hidden="true">
          <div class="sw-page-chrome__heading">
            <span class="placeholder col-3 d-block" style={{ height: '1.6rem', maxWidth: '10rem' }} />
            <span class="placeholder col-1 d-block" style={{ height: '1rem', maxWidth: '2rem' }} />
          </div>
          <div class="sw-page-chrome__actions">
            <span class="placeholder" style={{ height: '2rem', width: '9rem' }} />
          </div>
        </div>
        <div class="sw-tickets-list placeholder-glow" style="padding:0.75rem 0" aria-busy="true" aria-label={t('orders.loading')}>
          <div class="sw-tickets-table-wrap table-responsive">
            <table class="table table-sm align-middle sw-tickets-table">
              <thead>
                <tr>
                  <th>{t('orders.orderId')}</th>
                  <th>{t('orders.date')}</th>
                  <th>{t('orders.status')}</th>
                  <th class="text-end">{t('orders.total')}</th>
                  <th aria-hidden="true" />
                </tr>
              </thead>
              <tbody>
                {[1, 2, 3, 4, 5].map((i) => (
                  <tr key={i}>
                    <td><span class="placeholder col-8 d-block" style={{ height: '1rem' }} /></td>
                    <td><span class="placeholder col-6 d-block" style={{ height: '1rem' }} /></td>
                    <td><span class="placeholder col-4 d-block" style={{ height: '1.25rem' }} /></td>
                    <td class="text-end"><span class="placeholder col-5 d-block ms-auto" style={{ height: '1rem' }} /></td>
                    <td />
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div class="sw-tickets-shell">
        <div class="alert alert-danger mb-0">
          <span class="sw-ticket-detail__nav-label">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
              <circle cx="12" cy="12" r="9" stroke="currentColor" stroke-width="1.8" />
              <path d="M12 7v6" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" />
              <circle cx="12" cy="16.5" r="1" fill="currentColor" />
            </svg>
            {error}
          </span>
        </div>
      </div>
    );
  }

  return (
    <div class="sw-tickets-shell" data-sw-ui="orders-v3">
      <div class="sw-page-chrome">
        <div class="sw-page-chrome__heading">
          <h1 class="sw-page-chrome__title">{t('orders.title')}</h1>
          <span class="sw-page-chrome__meta">{orderList.length}</span>
        </div>
        <div class="sw-page-chrome__actions">
          <div class="sw-orders-filter">
            <label class="sw-orders-filter__label" for="sw-orders-filter">{t('orders.filter')}</label>
            <select
              id="sw-orders-filter"
              class="form-select form-select-sm"
              value={filter}
              onChange={(e) => setFilter(e.currentTarget.value)}
            >
              <option value="all">{t('orders.allTime')}</option>
              <option value="M">{t('orders.thisMonth')}</option>
              <option value="W">{t('orders.thisWeek')}</option>
              <option value="D">{t('orders.today')}</option>
            </select>
          </div>
        </div>
      </div>

      <div class="sw-tickets-list">
        {orderList.length === 0 ? (
          <div class="sw-tickets-list__empty alert alert-info text-center mb-0">{t('orders.empty')}</div>
        ) : (
          <>
            <div class="sw-tickets-desktop">
              <div class="sw-tickets-table-wrap table-responsive">
                <table class="table table-sm align-middle sw-tickets-table">
                  <thead>
                    <tr>
                      <th class="sw-tickets-table__subject">{t('orders.orderId')}</th>
                      <th class="sw-tickets-table__date">{t('orders.date')}</th>
                      <th class="sw-tickets-table__status">{t('orders.status')}</th>
                      <th class="sw-orders-table__total">{t('orders.total')}</th>
                      <th class="sw-tickets-table__action" scope="col"><span class="sw-tickets-table__action-label" aria-hidden="true" /></th>
                    </tr>
                  </thead>
                  <tbody>
                    {paginatedOrders.map((o: any) => {
                      const id = String(o.sorderid ?? o.orderid ?? o.id);
                      const attention = isOrderAttentionStatus(o.status);
                      return (
                        <tr
                          key={id}
                          class={`sw-tickets-table__row${attention ? ' sw-tickets-table__row--attention' : ''}`}
                          tabIndex={0}
                          role="button"
                          onClick={() => openOrder(id)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                              e.preventDefault();
                              openOrder(id);
                            }
                          }}
                        >
                          <td class="sw-tickets-table__subject">
                            <span class="sw-ticket-link">#{id}</span>
                          </td>
                          <td class="sw-tickets-table__date">
                            <OrderDateCell value={o.order_timestamp ?? o.date ?? o.order_date} />
                          </td>
                          <td class="sw-tickets-table__status">
                            <span class={getOrderStatusClass(o.status)}>{getOrderStatusLabel(o.status, t)}</span>
                          </td>
                          <td class="sw-orders-table__total">
                            <span class="sw-orders-total">{formatPrice(o.total)}</span>
                          </td>
                          <td class="sw-tickets-table__action"><OrderRowChevron /></td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>

            <div class="sw-tickets-mobile">
              {paginatedOrders.map((o: any) => {
                const id = String(o.sorderid ?? o.orderid ?? o.id);
                const attention = isOrderAttentionStatus(o.status);
                return (
                  <article
                    key={`m-${id}`}
                    class={`sw-ticket-card${attention ? ' sw-ticket-card--attention' : ''}`}
                    tabIndex={0}
                    role="button"
                    onClick={() => openOrder(id)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        openOrder(id);
                      }
                    }}
                  >
                    <div class="sw-ticket-card__head">
                      <span class="sw-ticket-link">#{id}</span>
                      <OrderRowChevron />
                    </div>
                    <div class="sw-ticket-card__row">
                      <span class={getOrderStatusClass(o.status)}>{getOrderStatusLabel(o.status, t)}</span>
                      <span class="sw-orders-total">{formatPrice(o.total)}</span>
                    </div>
                    <div class="sw-ticket-card__meta">
                      <span><strong>{t('orders.date')}:</strong> {formatTicketDateTime(o.order_timestamp ?? o.date ?? o.order_date).compact}</span>
                    </div>
                  </article>
                );
              })}
            </div>

            {totalPages > 1 && (
              <div class="sw-orders-pagination">
                <Pagination currentPage={page} totalPages={totalPages} onPageChange={setPage} />
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

function OrderDetailsView({ config, orderId, onBack }: { config: ReturnType<typeof getConfig>, orderId: string, onBack: () => void }) {
  const [order, setOrder] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    getOrder(config, orderId).then((res) => {
      setLoading(false);
      if (res.status === 200 && res.result) {
        setOrder(res.result);
      } else {
        setError(res.error ?? t('orders.detailsFailed'));
      }
    });
  }, [config.apiUrl, config.siteKey, orderId]);

  if (loading) return <OrderDetailsSkeleton />;
  if (error) {
    return (
      <div class="sw-tickets-shell">
        <div class="sw-ticket-detail">
          <div class="sw-ticket-detail__nav">
            <button class="btn btn-sm btn-outline-secondary" onClick={onBack}>
              <span class="sw-ticket-detail__nav-label">
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                  <path d="M15 18l-6-6 6-6" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" />
                </svg>
                {t('orders.back')}
              </span>
            </button>
          </div>
          <div class="alert alert-danger mb-0">{error}</div>
        </div>
      </div>
    );
  }
  if (!order) return null;

  const products = Array.isArray(order.products) && order.products.length
    ? order.products
    : [{
        product_title: order.product_title,
        productid: order.productid,
        weight_value: order.weight_value,
        set_value: order.set_value,
        amount: order.amount,
      }];

  const orderDate = formatTicketDateTime(order.order_timestamp ?? order.date ?? order.order_date);

  return (
    <div class="sw-tickets-shell">
      <div class="sw-ticket-detail">
        <div class="sw-ticket-detail__nav">
          <button class="btn btn-sm btn-outline-secondary" onClick={onBack}>
            <span class="sw-ticket-detail__nav-label">
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                <path d="M15 18l-6-6 6-6" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" />
              </svg>
              {t('orders.backToOrders')}
            </span>
          </button>
        </div>

        <header class="sw-ticket-detail__header">
          <div class="sw-ticket-detail__heading">
            <h1 class="sw-ticket-detail__subject">#{order.sorderid}</h1>
            <div class="sw-ticket-detail__meta">
              <span class={getOrderStatusClass(order.status)}>{getOrderStatusLabel(order.status, t)}</span>
              <time class="sw-ticket-detail__date" title={orderDate.title}>{orderDate.compact}</time>
            </div>
          </div>
          <div class="sw-order-detail__total-pill">
            <span class="sw-order-detail__total-label">{t('orders.total')}</span>
            <strong class="sw-order-detail__total-value">{formatPrice(order.total)}</strong>
          </div>
        </header>

        {(order.payment_link || order.paymentLink) ? (
          <div class="sw-order-detail__actions mb-3">
            <a href={String(order.payment_link || order.paymentLink)} class="btn btn-primary" rel="noopener noreferrer">
              {t('orders.payNow')}
            </a>
          </div>
        ) : (['Q', 'F', 'I'].includes(String(order.status || '').toUpperCase()) ? (
          <div class="sw-order-notice mb-3">
            <p class="sw-order-notice__text mb-2">{t('orders.awaitingConfirmation')}</p>
            <button
              type="button"
              class="btn btn-outline-secondary btn-sm"
              onClick={() => {
                const { viewUrls } = config;
                const url = viewUrls?.contactus || viewUrls?.contact || '/contactus';
                if (typeof window !== 'undefined') {
                  window.location.href = url.startsWith('http') ? url : new URL(url, window.location.origin).href;
                }
              }}
            >
              {t('orders.contactUs')}
            </button>
          </div>
        ) : null)}

        <section class="sw-order-section">
          <h2 class="sw-ticket-thread__title">{t('orders.details')}</h2>
          <div class="sw-order-section__card">
            <dl class="sw-order-kv-grid">
              <DetailRow label={t('orders.orderDate')}>{orderDate.compact}</DetailRow>
              <DetailRow label={t('orders.orderid')}>#{order.sorderid}</DetailRow>
              <DetailRow label={t('orders.statusLabel')}>
                <span class={getOrderStatusClass(order.status)}>{getOrderStatusLabel(order.status, t)}</span>
              </DetailRow>
              <DetailRow label={t('orders.paymentMethod')}>{order.payment_method || '—'}</DetailRow>
              <DetailRow label={t('orders.shipping')}>{order.shipping_method || '—'}</DetailRow>
              {order.tracking_number ? (
                <DetailRow label={t('orders.trackingNumber')}>{order.tracking_number}</DetailRow>
              ) : null}
              {order.tracking_number2 ? (
                <DetailRow label={t('orders.trackingExtra')}>{order.tracking_number2}</DetailRow>
              ) : null}
              {order.send_date ? (
                <DetailRow label={t('orders.sendDate')}>{order.send_date}</DetailRow>
              ) : null}
            </dl>
          </div>
        </section>

        <section class="sw-order-section">
          <h2 class="sw-ticket-thread__title">{t('orders.productsInfo')}</h2>
          <div class="sw-order-section__card sw-order-products">
            {products.map((product: any, idx: number) => (
              <article key={`${product.productid ?? 'p'}-${idx}`} class="sw-order-product">
                <div class="sw-order-product__title">
                  {product.product_title} {product.productid != null ? <span class="sw-order-product__id">#{product.productid}</span> : null}
                </div>
                <dl class="sw-order-kv-grid sw-order-kv-grid--compact">
                  <DetailRow label={t('orders.weight')}>{product.weight_value || '—'}</DetailRow>
                  <DetailRow label={t('orders.set')}>{product.set_value || '—'}</DetailRow>
                  <DetailRow label={t('orders.quantity')}>{product.amount || 1} {t('orders.items')}</DetailRow>
                </dl>
              </article>
            ))}
            {order.bonus_product ? (
              <article class="sw-order-product sw-order-product--bonus">
                <div class="sw-order-product__title">{t('orders.bonusProduct')}</div>
                <p class="sw-order-product__bonus mb-0">
                  {order.bonus_product.product || '—'} {order.bonus_product.weight_value} {order.bonus_product.set_value}
                </p>
              </article>
            ) : null}
          </div>
        </section>

        <section class="sw-order-section">
          <h2 class="sw-ticket-thread__title">{t('orders.orderInfo')}</h2>
          <div class="sw-order-section__card">
            <dl class="sw-order-kv-grid">
              <DetailRow label={t('orders.subtotal')}>{formatPrice(order.subtotal)}</DetailRow>
              {Number(order.discount) > 0 ? (
                <DetailRow label={t('orders.discount')}>{formatPrice(order.discount)}</DetailRow>
              ) : null}
              {order.coupon_type !== 'free_ship' && Number(order.coupon_discount) > 0 ? (
                <DetailRow label={t('orders.couponSaving')}>{formatPrice(order.coupon_discount)}</DetailRow>
              ) : null}
              {order.discounted_subtotal !== order.subtotal && order.discounted_subtotal ? (
                <DetailRow label={t('orders.discountedSubtotal')}>{formatPrice(order.discounted_subtotal)}</DetailRow>
              ) : null}
              <DetailRow label={t('orders.shippingCosts')}>{formatPrice(order.shipping_cost)}</DetailRow>
              {order.coupon && order.coupon_type === 'free_ship' ? (
                <DetailRow label={t('orders.couponSaving')}>
                  {formatPrice(order.coupon_discount)} ({order.coupon})
                </DetailRow>
              ) : null}
              {Number(order.bonus_discount) > 0 ? (
                <DetailRow label={t('orders.bonusDiscount')}>{formatPrice(order.bonus_discount)}</DetailRow>
              ) : null}
              <DetailRow label={t('orders.totalLabel')}>
                <strong class="sw-orders-total">{formatPrice(order.total)}</strong>
              </DetailRow>
            </dl>
          </div>
        </section>

        <section class="sw-order-section">
          <h2 class="sw-ticket-thread__title">{t('orders.customerInfo')}</h2>
          <div class="sw-order-section__card">
            <div class="sw-order-customer-blocks">
              <div>
                <h3 class="sw-order-customer__subtitle">{t('orders.personalInfoLabel')}</h3>
                <dl class="sw-order-kv-grid sw-order-kv-grid--compact">
                  <DetailRow label={t('orders.titleLabel')}>{order.title || '—'}</DetailRow>
                  <DetailRow label={t('orders.firstNameLabel')}>{order.firstname || '—'}</DetailRow>
                  <DetailRow label={t('orders.lastNameLabel')}>{order.lastname || '—'}</DetailRow>
                </dl>
              </div>
              <div>
                <h3 class="sw-order-customer__subtitle">{t('orders.billingAddressLabel')}</h3>
                <dl class="sw-order-kv-grid sw-order-kv-grid--compact">
                  <DetailRow label={t('orders.addressLabel')}>{order.b_address || '—'}</DetailRow>
                  <DetailRow label={t('orders.cityLabel')}>{order.b_city || '—'}</DetailRow>
                  <DetailRow label={t('orders.stateLabel')}>{order.b_statename || '—'}</DetailRow>
                  <DetailRow label={t('orders.zipcodeLabel')}>{order.b_zipcode || '—'}</DetailRow>
                  <DetailRow label={t('orders.countryLabel')}>{order.b_countryname || '—'}</DetailRow>
                </dl>
              </div>
              <div>
                <h3 class="sw-order-customer__subtitle">{t('orders.shippingAddressLabel')}</h3>
                <dl class="sw-order-kv-grid sw-order-kv-grid--compact">
                  <DetailRow label={t('orders.addressLabel')}>{order.s_address || '—'}</DetailRow>
                  <DetailRow label={t('orders.cityLabel')}>{order.s_city || '—'}</DetailRow>
                  <DetailRow label={t('orders.stateLabel')}>{order.s_statename || '—'}</DetailRow>
                  <DetailRow label={t('orders.zipcodeLabel')}>{order.s_zipcode || '—'}</DetailRow>
                  <DetailRow label={t('orders.countryLabel')}>{order.s_countryname || '—'}</DetailRow>
                </dl>
              </div>
              <div>
                <h3 class="sw-order-customer__subtitle">{t('orders.contactInfoLabel')}</h3>
                <dl class="sw-order-kv-grid sw-order-kv-grid--compact">
                  <DetailRow label={t('orders.phoneLabel')}>{order.phone || '—'}</DetailRow>
                  <DetailRow label={t('orders.emailLabel')}>
                    {order.email ? <a href={`mailto:${order.email}`}>{order.email}</a> : '—'}
                  </DetailRow>
                </dl>
              </div>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
}
